import { useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import useDatasets from "datasets/hooks/useDatasets";
import { DatasetListItem } from "datasets/types/interfaces/datasetListItem";
import { formatDistanceToNowStrict } from "date-fns";
import { ArrowRight, Plus, Search } from "lucide-react";
import pluralize from "pluralize";
import prettyBytes from "pretty-bytes";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Skeleton } from "~/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import useNextGenHeader from "~/contexts/nextgen-header";
import { usePageContext } from "~/contexts/page-context";
import useUser from "~/contexts/user";
import { useAgentList } from "~/hooks/useAgentList";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import { useFavorites } from "~/hooks/useFavorites";
import { useFetcher } from "~/hooks/useFetcher";
import useUserPermission from "~/hooks/useUserPermission";
import { NEXTGEN_MAX_CONTENT_WIDTH } from "~/lib/layout";
import useConversations from "~/pages/admin/hooks/useConversations";
import { DividedList } from "~/pages/manifold-nextgen/components/DividedList";
import { FavoriteButton } from "~/pages/manifold-nextgen/components/FavoriteButton";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import { AccessLevelBadge } from "~/pages/manifold-nextgen/components/datasets/AccessLevelBadge";
import { CreateDatasetModal } from "~/pages/manifold-nextgen/components/datasets/CreateDatasetModal";
import { DatasetIcon } from "~/pages/manifold-nextgen/components/datasets/DatasetIcon";
import { NewProjectDialog } from "~/pages/manifold-nextgen/components/projects/NewProjectDialog";
import { ProjectAccessBadge } from "~/pages/manifold-nextgen/components/projects/ProjectAccessBadge";
import { ProjectIcon } from "~/pages/manifold-nextgen/components/projects/ProjectIcon";
import { QuickActionsGrid } from "~/pages/manifold-nextgen/components/quick-actions/QuickActionsGrid";
import { useCanManageProjects } from "~/pages/manifold-nextgen/hooks/useCanManageProjects";
import { useCanUseProjects } from "~/pages/manifold-nextgen/hooks/useCanUseProjects";
import { nextgenConversationPath, nextgenProjectPath } from "~/pages/manifold-nextgen/paths";
import { nextGenDatasetDetailPath, ROUTE_NOVA_DATASETS } from "~/router/constants";
import type { Agent } from "~/services/agents";
import { ProjectList, ProjectListItem, projectListPath, projectRouteId } from "~/services/projects";
import { QuickActionPlacement } from "~/services/quickActions";
import { stripEmphasis, type SessionSearchResult, useSearchSessions } from "./useSearchSessions";

const PREVIEW_LIMIT = 5;
const RECENT_SESSIONS_LIMIT = 8;
const SEARCH_RESULT_LIMIT = 5;

// Stable fallbacks for while data is loading. A fresh `[]` literal on every
// render would change reference each time, cascading through the useMemos
// that build `heroContent` and re-triggering the header's setHeroContent
// effect every render (the effect's equality check can't treat the JSX's
// inline event handlers as equal across renders, so it always sees "new"
// content) — an infinite render loop.
const EMPTY_PROJECTS: ProjectListItem[] = [];
const EMPTY_DATASETS: DatasetListItem[] = [];

// A session row names its project by uuid in `resource_id`, but a project's
// URL is addressed by its short_id — the only form the analysis-agent's
// create-session route accepts. The search enrichment resolves the short_id
// alongside the row, so linking by it costs no extra request. Falls back to the
// uuid for a row whose project didn't resolve, which ProjectLayout canonicalizes
// on arrival.
const projectLinkId = (session: { projectShortId?: string | null; resourceId: string }) =>
  session.projectShortId ?? session.resourceId;

// Sized to PREVIEW_LIMIT so the skeleton holds the height the resolved list
// will occupy. `DividedList` keys off `id`.
const SKELETON_ROWS = Array.from({ length: PREVIEW_LIMIT }, (_, id) => ({ id }));

const greetingForHour = (hour: number): string => {
  if (hour < 12) {
    return "Good morning";
  }
  if (hour < 18) {
    return "Good afternoon";
  }
  return "Good evening";
};

const matchesQuery = (haystack: string, query: string) => haystack.toLowerCase().includes(query);

const SeeAllLink = ({ to, count }: { to: string; count: number }) => (
  <Link
    to={to}
    className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
  >
    See all {count}
    <ArrowRight className="size-3" aria-hidden="true" />
  </Link>
);

interface HomeProjectRowProps {
  project: ProjectListItem;
  favorite: boolean;
  onToggleFavorite: () => void;
}

// Compact row for the Home preview: dataset/agent/updated-at summary in the
// subtext instead of avatar stacks, an owner chip (shown only when the
// caller owns the project) next to the favorite toggle. Home-specific — the
// shared ProjectCard's `view="list"` (used on the real Projects page) is
// untouched.
const HomeProjectRow = ({ project, favorite, onToggleFavorite }: HomeProjectRowProps) => {
  const lastActivity = formatDistanceToNowStrict(new Date(project.last_activity_at), {
    addSuffix: true,
  });

  return (
    <Link
      to={nextgenProjectPath(projectRouteId(project))}
      className="flex items-center gap-2 px-4 py-3 transition-colors hover:bg-muted"
    >
      <ProjectIcon project={project} size={20} />
      <div className="min-w-0 flex-1">
        <h3 className="truncate text-sm font-semibold text-card-foreground">{project.name}</h3>
        <p className="truncate text-xs text-muted-foreground">
          {project.dataset_ids.length} {pluralize("dataset", project.dataset_ids.length)} ·{" "}
          {project.agents.length} {pluralize("agent", project.agents.length)} · {lastActivity}
        </p>
      </div>
      <ProjectAccessBadge accessLevel={project.access_level} hideDefault />
      <FavoriteButton favorite={favorite} onToggle={onToggleFavorite} />
    </Link>
  );
};

interface HomeDatasetRowProps {
  dataset: DatasetListItem;
  favorite: boolean;
  onToggleFavorite: () => void;
}

// Compact row for the Home preview: size/updated-at summary in the subtext
// instead of table/byte-size stat chips, an owner chip (shown only when the
// caller owns the dataset) next to the favorite toggle. Home-specific — the
// shared DatasetCard's `view="list"` (used on the real Datasets page) is
// untouched.
const HomeDatasetRow = ({ dataset, favorite, onToggleFavorite }: HomeDatasetRowProps) => {
  const updatedAt = formatDistanceToNowStrict(new Date(dataset.updated_at), { addSuffix: true });

  return (
    <Link
      to={nextGenDatasetDetailPath(dataset.id)}
      className="flex items-center gap-2 px-4 py-3 transition-colors hover:bg-muted"
    >
      <DatasetIcon dataset={dataset} size={20} />
      <div className="min-w-0 flex-1">
        <h3 className="truncate text-sm font-semibold text-card-foreground">{dataset.title}</h3>
        <p className="truncate text-xs text-muted-foreground">
          {prettyBytes(dataset.components?.tables?.total_bytes ?? 0)} · {updatedAt}
        </p>
      </div>
      <AccessLevelBadge accessLevel={dataset.access_level} hideDefault />
      <FavoriteButton favorite={favorite} onToggle={onToggleFavorite} />
    </Link>
  );
};

// Mirrors `HomeProjectRow` / `HomeDatasetRow`. The bars and the gap between
// them total 36px, the height of the real row's two text lines, so nothing
// shifts when data lands. No access badge — it's `hideDefault`, so most rows
// don't carry one.
const PreviewRowSkeleton = () => (
  <div className="flex items-center gap-2 px-4 py-3">
    <Skeleton className="size-5 shrink-0 rounded-md" />
    <div className="flex min-w-0 flex-1 flex-col gap-2.5">
      <Skeleton className="h-3.5 w-1/3" />
      <Skeleton className="h-3 w-2/3" />
    </div>
    <Skeleton className="size-7 shrink-0 rounded-md" />
  </div>
);

// Goes through `DividedList` so the placeholders sit in the same bordered,
// hairline-divided container the real preview uses. That also gives them list
// semantics, hence the `aria-hidden`: the status label is the message, not five
// empty rows.
const PreviewSkeleton = ({ "aria-label": ariaLabel }: { "aria-label": string }) => (
  <div role="status" aria-label={ariaLabel}>
    <div aria-hidden="true">
      <DividedList
        items={SKELETON_ROWS}
        className="overflow-hidden rounded-xl border border-border"
        renderItem={() => <PreviewRowSkeleton />}
      />
    </div>
  </div>
);

// Mirrors a recent-session tile: round agent avatar beside the same two-line
// block, in a card of the same padding and radius.
const RecentSessionsSkeleton = () => (
  <div role="status" aria-label="Loading recent sessions">
    <div aria-hidden="true" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: RECENT_SESSIONS_LIMIT }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-3"
        >
          <Skeleton className="size-7 shrink-0 rounded-full" />
          <div className="flex min-w-0 flex-1 flex-col gap-2.5">
            <Skeleton className="h-3.5 w-2/3" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

interface SearchDropdownProps {
  query: string;
  projects: ProjectListItem[];
  datasets: DatasetListItem[];
  sessions: SessionSearchResult[];
  agentsById: Record<string, Agent>;
  sessionsLoading: boolean;
  sessionsHasMore: boolean;
  onLoadMoreSessions: () => void;
  onNavigate: () => void;
}

// Command-palette-style results under the search box: grouped by entity
// type, navigating away closes it via `onNavigate` clearing the query.
const SearchDropdown = ({
  query,
  projects,
  datasets,
  sessions,
  agentsById,
  sessionsLoading,
  sessionsHasMore,
  onLoadMoreSessions,
  onNavigate,
}: SearchDropdownProps) => {
  const hasResults = projects.length > 0 || datasets.length > 0 || sessions.length > 0;

  return (
    <div
      role="listbox"
      className="absolute top-[calc(100%+8px)] right-0 left-0 z-50 max-h-[340px] overflow-y-auto rounded-2xl border border-border bg-popover text-left text-popover-foreground shadow-2xl"
    >
      {!hasResults && !sessionsLoading && (
        <p className="px-4 py-6 text-center text-sm text-muted-foreground">
          No matches for &quot;{query}&quot;
        </p>
      )}
      {(sessions.length > 0 || sessionsLoading) && (
        <div className="p-2" data-testid="session-results">
          <div className="px-2 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Sessions
          </div>
          {sessionsLoading && sessions.length === 0 ? (
            <p className="px-2 py-2 text-xs text-muted-foreground">Searching sessions…</p>
          ) : (
            sessions.map((session) => (
              <Link
                key={session.id}
                to={nextgenConversationPath(projectLinkId(session), session.id)}
                onClick={onNavigate}
                className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-muted"
              >
                <span data-testid={`session-result-avatar-${session.id}`}>
                  <AgentAvatar
                    agent={session.agentId ? agentsById[session.agentId] : undefined}
                    size={28}
                    aria-hidden
                  />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-foreground">
                    {session.snippet ? stripEmphasis(session.snippet) : session.displayName}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    {session.projectName}
                    {session.projectName && " · "}
                    {formatDistanceToNowStrict(new Date(session.updatedAt), { addSuffix: true })}
                  </div>
                </div>
              </Link>
            ))
          )}
          {sessionsHasMore && (
            <button
              type="button"
              onClick={onLoadMoreSessions}
              className="mt-1 w-full rounded-lg px-2 py-2 text-left text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              Load more
            </button>
          )}
        </div>
      )}
      {projects.length > 0 && (
        <div className="p-2">
          <div className="px-2 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Projects
          </div>
          {projects.map((project) => (
            <Link
              key={project.id}
              to={nextgenProjectPath(projectRouteId(project))}
              onClick={onNavigate}
              className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-muted"
            >
              <ProjectIcon project={project} size={20} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-foreground">{project.name}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {project.dataset_ids.length} {pluralize("dataset", project.dataset_ids.length)}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
      {datasets.length > 0 && (
        <div className="p-2">
          <div className="px-2 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Datasets
          </div>
          {datasets.map((dataset) => (
            <Link
              key={dataset.id}
              to={nextGenDatasetDetailPath(dataset.id)}
              onClick={onNavigate}
              className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-muted"
            >
              <DatasetIcon dataset={dataset} size={20} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-foreground">
                  {dataset.title}
                </div>
                <div className="truncate text-xs text-muted-foreground">
                  {dataset.components?.tables?.count ?? 0}{" "}
                  {pluralize("table", dataset.components?.tables?.count ?? 0)}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export const Home = () => {
  useDocumentTitle("Home");
  const user = useUser();
  const [search, setSearch] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isNewProjectOpen, setIsNewProjectOpen] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const query = search.trim().toLowerCase();
  const hasCreateDatasetPermission = useUserPermission("create:catalog-dataset");
  const canUseProjects = useCanUseProjects();
  const canManageProjects = useCanManageProjects();

  const { data: projectList, isLoading: projectsLoading } =
    useFetcher<ProjectList>(projectListPath());
  const { data: datasets, isLoading: datasetsLoading } = useDatasets();
  const { data: agentList } = useAgentList();
  const { data: recentConversations, isLoading: recentSessionsLoading } = useConversations({
    isAdmin: false,
    nextgenOnly: true,
    limit: RECENT_SESSIONS_LIMIT,
    sortKey: "updated_at",
    sortOrder: "desc",
  });
  const {
    results: sessionResults,
    hasMore: sessionsHasMore,
    isLoading: sessionsLoading,
    loadMore: loadMoreSessions,
  } = useSearchSessions(search);

  const allProjects = projectList?.items ?? EMPTY_PROJECTS;
  const allDatasets = datasets ?? EMPTY_DATASETS;
  const agentsById = useMemo(
    () => Object.fromEntries((agentList?.items ?? []).map((agent) => [agent.id, agent])),
    [agentList]
  );

  const {
    favorites: favoriteProjects,
    others: otherProjects,
    toggleFavorite: toggleFavoriteProject,
    isFavorite: isFavoriteProject,
  } = useFavorites("project-favorites", allProjects, (p) => p.id);
  const {
    favorites: favoriteDatasets,
    others: otherDatasets,
    toggleFavorite: toggleFavoriteDataset,
    isFavorite: isFavoriteDataset,
  } = useFavorites("dataset-favorites", allDatasets, (d) => d.id);

  const previewProjects = useMemo(
    () => [...favoriteProjects, ...otherProjects].slice(0, PREVIEW_LIMIT),
    [favoriteProjects, otherProjects]
  );
  const previewDatasets = useMemo(
    () => [...favoriteDatasets, ...otherDatasets].slice(0, PREVIEW_LIMIT),
    [favoriteDatasets, otherDatasets]
  );

  const searchProjects = useMemo(() => {
    if (!query) {
      return [];
    }
    return allProjects.filter((p) => matchesQuery(p.name, query)).slice(0, SEARCH_RESULT_LIMIT);
  }, [allProjects, query]);

  const searchDatasets = useMemo(() => {
    if (!query) {
      return [];
    }
    return allDatasets
      .filter((d) => matchesQuery(d.title, query) || matchesQuery(d.description ?? "", query))
      .slice(0, SEARCH_RESULT_LIMIT);
  }, [allDatasets, query]);

  const closeSearch = () => {
    setIsSearchOpen(false);
    setSearch("");
  };

  const greeting = useMemo(() => greetingForHour(new Date().getHours()), []);
  const name = user?.given_name || user?.name || "there";

  const heroContent = useMemo(
    () => (
      <div className="mx-auto flex w-full max-w-[660px] flex-col items-center gap-1 px-4 pt-6 pb-12 text-center">
        <h1 className="text-2xl font-bold tracking-tight text-white">
          {greeting}, {name}.
        </h1>
        <p className="text-sm text-[#C4B5FD]">What are you working on today?</p>
        <div
          ref={searchContainerRef}
          className="relative mt-3 w-full"
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) {
              setIsSearchOpen(false);
            }
          }}
        >
          <Search
            className="pointer-events-none absolute left-4 top-1/2 size-5 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            type="search"
            placeholder="Search sessions, projects, and datasets..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setIsSearchOpen(e.target.value.trim().length > 0);
            }}
            onFocus={() => {
              if (search.trim()) {
                setIsSearchOpen(true);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setIsSearchOpen(false);
              }
            }}
            aria-label="Search sessions, projects, and datasets"
            className="h-13 rounded-xl border-none bg-white pl-12 text-base text-foreground shadow-lg"
          />
          {isSearchOpen && query && (
            <SearchDropdown
              query={query}
              projects={searchProjects}
              datasets={searchDatasets}
              sessions={sessionResults}
              agentsById={agentsById}
              sessionsLoading={sessionsLoading}
              sessionsHasMore={sessionsHasMore}
              onLoadMoreSessions={loadMoreSessions}
              onNavigate={closeSearch}
            />
          )}
        </div>
      </div>
    ),
    [
      greeting,
      name,
      search,
      isSearchOpen,
      query,
      searchProjects,
      searchDatasets,
      sessionResults,
      agentsById,
      sessionsLoading,
      sessionsHasMore,
      loadMoreSessions,
    ]
  );

  useNextGenHeader({ variant: "colored", heroContent });
  usePageContext({ page: "home" });

  return (
    <div
      className="mx-auto flex w-full flex-col gap-9 px-4 py-6"
      style={{ maxWidth: NEXTGEN_MAX_CONTENT_WIDTH }}
    >
      <QuickActionsGrid placement={QuickActionPlacement.HOME} />

      <section className="flex flex-col gap-3" aria-labelledby="home-recent-sessions-heading">
        <h2 id="home-recent-sessions-heading" className="text-[17px] font-semibold text-foreground">
          Recent sessions
        </h2>
        {recentSessionsLoading ? (
          <RecentSessionsSkeleton />
        ) : recentConversations.length === 0 ? (
          <p className="text-sm text-muted-foreground">No recent sessions yet</p>
        ) : (
          <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" role="list">
            {recentConversations.map((conversation) => (
              <li key={conversation.id}>
                <Link
                  to={nextgenConversationPath(projectLinkId(conversation), conversation.id)}
                  className="flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-3 transition-colors hover:border-primary/40 hover:bg-muted"
                >
                  <span data-testid={`recent-session-avatar-${conversation.id}`}>
                    <AgentAvatar
                      agent={conversation.agentId ? agentsById[conversation.agentId] : undefined}
                      size={28}
                      aria-hidden
                    />
                  </span>
                  <div className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-foreground">
                      {conversation.displayName}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {conversation.projectName}
                      {conversation.projectName && " · "}
                      {formatDistanceToNowStrict(new Date(conversation.updatedAt), {
                        addSuffix: true,
                      })}
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="grid gap-6 md:grid-cols-2">
        <section className="flex flex-col gap-3" aria-labelledby="home-datasets-heading">
          <div className="flex items-center justify-between">
            <div className="flex items-baseline gap-2">
              <h2 id="home-datasets-heading" className="text-[17px] font-semibold text-foreground">
                Datasets
              </h2>
              {!datasetsLoading && (
                <SeeAllLink to={ROUTE_NOVA_DATASETS} count={allDatasets.length} />
              )}
            </div>
            {hasCreateDatasetPermission && (
              <Tooltip>
                <CreateDatasetModal
                  trigger={
                    <TooltipTrigger asChild>
                      <Button
                        aria-label="New dataset"
                        variant="ghost"
                        size="icon"
                        className="size-8"
                      >
                        <Plus aria-hidden="true" />
                      </Button>
                    </TooltipTrigger>
                  }
                />
                <TooltipContent>Create a dataset</TooltipContent>
              </Tooltip>
            )}
          </div>
          {datasetsLoading ? (
            <PreviewSkeleton aria-label="Loading datasets" />
          ) : previewDatasets.length === 0 ? (
            <p className="text-sm text-muted-foreground">No datasets yet</p>
          ) : (
            <DividedList
              items={previewDatasets}
              className="overflow-hidden rounded-xl border border-border"
              renderItem={(dataset) => (
                <HomeDatasetRow
                  dataset={dataset}
                  favorite={isFavoriteDataset(dataset.id)}
                  onToggleFavorite={() => toggleFavoriteDataset(dataset.id)}
                />
              )}
            />
          )}
        </section>

        {canUseProjects && (
          <section className="flex flex-col gap-3" aria-labelledby="home-projects-heading">
            <div className="flex items-center justify-between">
              <div className="flex items-baseline gap-2">
                <h2
                  id="home-projects-heading"
                  className="text-[17px] font-semibold text-foreground"
                >
                  Projects
                </h2>
                {!projectsLoading && <SeeAllLink to="/nova/projects" count={allProjects.length} />}
              </div>
              {canManageProjects && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      aria-label="New project"
                      variant="ghost"
                      size="icon"
                      className="size-8"
                      onClick={() => setIsNewProjectOpen(true)}
                    >
                      <Plus aria-hidden="true" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Create a project</TooltipContent>
                </Tooltip>
              )}
            </div>
            {projectsLoading ? (
              <PreviewSkeleton aria-label="Loading projects" />
            ) : previewProjects.length === 0 ? (
              <p className="text-sm text-muted-foreground">No projects yet</p>
            ) : (
              <DividedList
                items={previewProjects}
                className="overflow-hidden rounded-xl border border-border"
                renderItem={(project) => (
                  <HomeProjectRow
                    project={project}
                    favorite={isFavoriteProject(project.id)}
                    onToggleFavorite={() => toggleFavoriteProject(project.id)}
                  />
                )}
              />
            )}
          </section>
        )}
      </div>

      <NewProjectDialog open={isNewProjectOpen} onOpenChange={setIsNewProjectOpen} />
    </div>
  );
};
